import * as React from "react";
import * as zagSwitch from "@zag-js/switch";
import { useMachine, normalizeProps } from "@zag-js/react";
import * as s from "./Switch.style.css";
import type { SwitchColor } from "./Switch.types";

export type { SwitchColor } from "./Switch.types";

export type SwitchProps = {
  id?: string;
  label?: string;
  disabled?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (v: boolean) => void;
  color?: SwitchColor;
  /** When false, label is shown without appending ": On/Off". Default true. */
  showStateText?: boolean;
  /** When true, only the toggle is shown (label used for accessibility). */
  hideLabel?: boolean;
};

export const Switch = ({
  id,
  label = "Toggle",
  disabled,
  checked,
  defaultChecked,
  onCheckedChange,
  color = "primary",
  showStateText = true,
  hideLabel = false,
}: SwitchProps) => {
  const reactId = React.useId();
  const isControlled = checked !== undefined;
  const onCheckedChangeRef = React.useRef(onCheckedChange);
  onCheckedChangeRef.current = onCheckedChange;

  const [state, send] = useMachine(
    zagSwitch.machine({
      id: id ?? reactId,
      disabled,
      checked: isControlled ? checked : defaultChecked,
      onCheckedChange(details) {
        onCheckedChangeRef.current?.(details.checked);
      },
    }),
  );

  const api = zagSwitch.connect(state, send, normalizeProps);

  React.useLayoutEffect(() => {
    send({ type: "CONTEXT.SET", context: { disabled } });
  }, [disabled, send]);

  React.useLayoutEffect(() => {
    if (!isControlled) return;
    if (state.context.checked !== !!checked) {
      send({ type: "CHECKED.SET", checked: !!checked, src: "controlled" });
    }
  }, [isControlled, checked, send, state.context.checked]);

  const rootProps = api.getRootProps();
  return (
    <label
      {...rootProps}
      className={s.root}
      aria-label={hideLabel ? label : rootProps["aria-label"]}
    >
      <input {...api.getHiddenInputProps()} />
      <span {...api.getControlProps()} className={s.control({ color })}>
        <span
          {...api.getThumbProps()}
          className={s.thumb({ checked: api.checked })}
        />
      </span>
      {!hideLabel && (
        <span {...api.getLabelProps()} className={s.textLabel}>
          {showStateText ? `${label}: ${api.checked ? "On" : "Off"}` : label}
        </span>
      )}
    </label>
  );
};
